import React, { useCallback } from 'react';
import { DateTime } from 'luxon';
import { Alert, ListGroup } from 'react-bootstrap';
import { ActionCheckItem } from './ActionCheckItem.js';
import { ActionWidgetFormPlaceholder } from './helpers/ActionWidgetFormPlaceholder.js';
import { useTranslation, Trans } from 'react-i18next';
import { useGetActionTypes } from '../../hooks/GetActions.js';
import { useGetActionEntriesForDay } from '../../hooks/GetActionEntries.js';
import { queueLocalActionEntryCreate } from '@soulhx/client-store';

interface IActionWidgetForm {
  date: DateTime;
  showDetails: boolean;
  showLongNames?: boolean;
}
export const ActionWidgetForm = ({ date, showDetails, showLongNames = true }: IActionWidgetForm) => {
  const { entries, entriesLoading } = useGetActionEntriesForDay(
    date.year.toString(),
    date.month.toString(),
    date.day.toString()
  );
  const { actionTypes, actionTypesLoading } = useGetActionTypes({ longNames: showLongNames });
  const { t } = useTranslation(['do']);

  const itemClicked = useCallback(
    async (id: string) => {
      const currentTime = DateTime.now();
      const dateToUse = DateTime.fromObject({
        year: date.year,
        month: date.month,
        day: date.day,
        hour: currentTime.hour,
        minute: currentTime.minute,
        second: currentTime.second,
        millisecond: currentTime.millisecond,
      });

      await queueLocalActionEntryCreate(id, true, dateToUse.toISO()!);
    },
    [date]
  );

  if (entriesLoading || actionTypesLoading) {
    return <ActionWidgetFormPlaceholder />;
  }

  if (actionTypes!.length > 0) {
    return (
      <ListGroup className="p-1" variant="flush">
        {actionTypes!.map((item) => (
          <ActionCheckItem
            key={item.id}
            item={item}
            clickFunction={itemClicked}
            showDetails={showDetails}
            completedEntries={entries!.filter((ad) => ad.actionType === item.id && ad.completed)}
          />
        ))}
      </ListGroup>
    );
  }

  return (
    <Alert>
      <p>{t('do:actionsWidget.noActionsAvailable')}</p>{' '}
      <p>
        <Trans i18nKey="do:actionsWidget.noActionsAvailableLink">
          Go to <Alert.Link href="/do/config">the Do page</Alert.Link> to set some up.
        </Trans>
      </p>
    </Alert>
  );
};
